import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { EyeOff, Gavel, Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { RelativeTime } from '@/components/DataDisplay'
import { fetchUserStrikes, issueStrike, type AdminUserStrikes } from '@/lib/adapters'
import { CardBackLink, Body, ActionCard, ReasonInput } from './userActionKit'
import {
  USERS_STR as S,
  fetchShadowbanState,
  setShadowban,
  isPending,
} from './usersAdminApi'

function sevClass(n: number): string {
  if (n >= 4) return 'text-danger'
  if (n === 3) return 'text-warning'
  return 'text-success'
}

/**
 * A3.27 — Cheklov qo'yish (strayk). JONLI `POST /admin/users/:id/strike` +
 * `GET /admin/users/:id/strikes`. Progressiv jazo: aktiv ballar yig'ilib borad;
 * konsol joriy strayklarni ko'rsatadi va yangi strayk beradi (og'irlik + sabab).
 */
export function UserRestrict() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [data, setData] = useState<AdminUserStrikes | null>(null)
  const [severity, setSeverity] = useState(1)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    const ac = new AbortController()
    fetchUserStrikes(userId, ac.signal)
      .then(setData)
      .catch(() => setData({ strikes: [], active_points: 0 }))
    return () => ac.abort()
  }, [userId])

  useEffect(() => load(), [load])

  const submit = async () => {
    if (reason.trim().length < 3 || busy) return
    setBusy(true)
    try {
      await issueStrike(userId, severity, reason.trim())
      setReason('')
      toast(S.issued, { tone: 'success' })
      load()
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.restrictTitle} screenId="A3.27" actions={<CardBackLink id={id} />} />
      <Body>
        <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-2.5">
          <span className="text-xs text-tertiary">{S.activePoints}</span>
          <span
            className={cn(
              'font-mono text-xl tabular',
              (data?.active_points ?? 0) >= 4 ? 'text-danger' : 'text-primary',
            )}
          >
            {data?.active_points ?? 0}
          </span>
        </div>

        <ActionCard title={S.issue} hint={S.restrictHint}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={severity}
              onChange={(e) => setSeverity(Number(e.target.value))}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
            >
              {[1, 2, 3, 4, 5].map((s) => (
                <option key={s} value={s}>
                  {S.severity} {s}
                </option>
              ))}
            </select>
          </div>
          <ReasonInput value={reason} onChange={setReason} />
          <button
            onClick={() => void submit()}
            disabled={busy || reason.trim().length < 3}
            className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-4 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
            {S.issue}
          </button>
        </ActionCard>

        {data && data.strikes.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-border-default">
            {data.strikes.map((s, i) => (
              <div
                key={s.id}
                className={cn('flex items-center gap-3 px-3 py-2 text-sm', i > 0 && 'border-t border-border-subtle')}
              >
                <span className={cn('w-6 shrink-0 text-center font-mono font-medium', sevClass(s.severity))}>
                  {s.severity}
                </span>
                <span className="min-w-0 flex-1 truncate text-primary">{s.reason}</span>
                <span className="shrink-0 text-xs text-tertiary">
                  <RelativeTime value={s.created_at} />
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </Body>
    </>
  )
}

/**
 * A3.30 — Shadowban (spam uchun jim cheklov). Foydalanuvchi post qiladi deб
 * o'ylaydi, lekin boshqalarga ko'rinmaydi; unga bildirilmaydi. Greenfield GET
 * holat + POST toggle (`/admin/users/:id/shadowban`) → 404 bo'lsa «kutilmoqda».
 */
export function UserShadowban() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [on, setOn] = useState<boolean | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    const ac = new AbortController()
    fetchShadowbanState(userId, ac.signal)
      .then(setOn)
      .catch(() => setOn(false))
    return () => ac.abort()
  }, [userId])

  useEffect(() => load(), [load])

  const toggle = async (enabled: boolean) => {
    if (reason.trim().length < 3 || busy) return
    setBusy(true)
    try {
      await setShadowban(userId, enabled, reason.trim())
      setOn(enabled)
      setReason('')
      toast(S.done, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.shadowTitle} screenId="A3.30" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.shadowHint}>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
                on
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                  : 'border-border-default bg-surface-1 text-tertiary',
              )}
            >
              <EyeOff size={13} />
              {on === null ? '…' : on ? S.shadowOn : S.shadowOff}
            </span>
          </div>
          <ReasonInput value={reason} onChange={setReason} />
          <div className="flex items-center gap-2">
            <button
              onClick={() => void toggle(true)}
              disabled={busy || on === true || reason.trim().length < 3}
              className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-4 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <EyeOff size={14} />}
              {S.shadowEnable}
            </button>
            <button
              onClick={() => void toggle(false)}
              disabled={busy || on === false || reason.trim().length < 3}
              className="rounded-md border border-border-default bg-surface-1 px-4 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
            >
              {S.shadowDisable}
            </button>
          </div>
        </ActionCard>
      </Body>
    </>
  )
}
